import { List } from "immutable";
import { ICustomProductData } from "common/records/CustomProduct";
import { IEstimateGroupData } from "common/records/EstimateGroup";
import { OpeningRecord } from "common/records/Opening";
import { FormUpdateEvent } from "common/reducers/rsfForm.actions";
import { ActionsUnion, createAction } from "common/reducers/UtilsTypes";

/**
 * A template line-item option, resolved to the ids the reducer needs. The reducer derives the
 * option's display/price fields from the resolved ProductOption in the store (parity with
 * ADD_ESTIMATE_LINE_ITEM_OPTION), so only the resolved ids travel in the payload.
 */
export interface ITemplateLineItemOptionData {
  productOptionId: number;
  productOptionGroupId: number;
}

/**
 * A custom (non-product) template line item. No price-list resolution — its fields are stored
 * inline on the template and applied as-is.
 */
export interface ITemplateCustomLineItemData {
  name: string;
  description: string;
  productPrice: number;
  laborPrice: number;
  uom: string;
  quantity: number;
  uuid?: string;
}

/**
 * A template line item, resolved by the async action against the estimate's activated price list.
 * Product-backed items carry the resolved `productId` (+ option ids); the reducer derives all
 * line-item fields from the resolved product via `productLineItemFields` / `deriveLineItemQuantity`
 * (same source-of-truth as an interactive product add — rsf-app `AddEstimateLineItemCmd`). Custom
 * items carry their inline fields in `custom`.
 */
export interface ITemplateLineItemData {
  productId?: number;
  productUuid?: string;
  options: ITemplateLineItemOptionData[];
  custom?: ITemplateCustomLineItemData;
}

/**
 * Data structure for a group to be added/merged from a template.
 */
export interface ITemplateGroupData {
  name: string;
  included: boolean;
  sortOrder: number;
  lineItems: ITemplateLineItemData[];
}

/**
 * How a template's groups combine with the estimate's existing groups:
 * - "merge" — sweep existing zero-line-item groups, then find-or-create by (name + included)
 * - "replace" — remove all existing groups first, preserving estimate-level config
 */
export type ApplyTemplateMode = "merge" | "replace";

/**
 * Fully hydrated template data ready to be applied to an estimate.
 * The async action is responsible for loading the template and resolving
 * all product/option UUIDs to their IDs via the activated price list.
 */
export interface IEstimateTemplateToApply {
  id: number;
  name: string;
  groups: ITemplateGroupData[];
}

/**
 * The kind/type of estimate being created.
 * - "default" - Standard pricing estimate (default)
 * - "inspection" - Inspection estimate
 * - "survey" - Survey estimate
 */
export type EstimateKind = "default" | "inspection" | "survey";

/**
 * Configuration for a group to be created with a new estimate.
 * Requires name and included status.
 */
export interface IEstimateGroupConfig {
  name: string;
  included: boolean;
}

/**
 * Default groups configuration used when no config is provided
 * and no org preference is set.
 */
export const DEFAULT_ESTIMATE_GROUPS_CONFIG: IEstimateGroupConfig[] = [
  { name: "Included", included: true },
  { name: "Options", included: false },
];

// Estimates
export const CREATE_ESTIMATE = "@estimateEditor/CREATE_ESTIMATE";
export const DUPLICATE_ESTIMATE = "@estimateEditor/DUPLICATE_ESTIMATE";
export const APPLY_ESTIMATE_TEMPLATE = "@estimateEditor/APPLY_ESTIMATE_TEMPLATE";
export const UPDATE_ESTIMATE = "@estimateEditor/UPDATE_ESTIMATE";
export const START_LINE_ITEM_SESSION = "@estimateEditor/START_LINE_ITEM_SESSION";
export const MARK_LINE_ITEM_DRAFT = "@estimateEditor/MARK_LINE_ITEM_DRAFT";
export const CANCEL_LINE_ITEM_SESSION = "@estimateEditor/CANCEL_LINE_ITEM_SESSION";
export const COMMIT_LINE_ITEM_SESSION = "@estimateEditor/COMMIT_LINE_ITEM_SESSION";
export const CLEANUP_DRAFT_LINE_ITEMS = "@estimateEditor/CLEANUP_DRAFT_LINE_ITEMS";
export const RECALCULATE_ESTIMATE_TOTALS = "@estimateEditor/RECALCULATE_ESTIMATE_TOTALS";
export const COMMIT_GROUP_TOTALS_TO_LAST_SAVED = "@estimateEditor/COMMIT_GROUP_TOTALS_TO_LAST_SAVED";
export const RESET_ESTIMATE = "@estimateEditor/RESET_ESTIMATE";

// Estimate Groups
export const ADD_ESTIMATE_GROUP = "@estimateEditor/ADD_ESTIMATE_GROUP";
export const REMOVE_ESTIMATE_GROUP = "@estimateEditor/REMOVE_ESTIMATE_GROUP";
export const UPDATE_ESTIMATE_GROUP = "@estimateEditor/UPDATE_ESTIMATE_GROUP";
export const REORDER_ESTIMATE_GROUP = "@estimateEditor/REORDER_ESTIMATE_GROUP";
export const REORDER_GROUPS_IN_BLOCK = "@estimateEditor/REORDER_GROUPS_IN_BLOCK";

// Estimate Line Items
export const LINE_ITEM_DRAG_END = "@estimateEditor/LINE_ITEM_DRAG_END";
export const ADD_ESTIMATE_LINE_ITEM = "@estimateEditor/ADD_ESTIMATE_LINE_ITEM";
export const ADD_CUSTOM_ESTIMATE_LINE_ITEM = "@estimateEditor/ADD_CUSTOM_ESTIMATE_LINE_ITEM";
export const START_ADDING_LINE_ITEM = "@estimateEditor/START_ADDING_LINE_ITEM";
export const SET_ESTIMATE_LINE_ITEM_PRODUCT = "@estimateEditor/SET_ESTIMATE_LINE_ITEM_PRODUCT";
export const REMOVE_ESTIMATE_LINE_ITEM = "@estimateEditor/REMOVE_ESTIMATE_LINE_ITEM";
export const UPDATE_ESTIMATE_LINE_ITEM = "@estimateEditor/UPDATE_ESTIMATE_LINE_ITEM";
export const UPDATE_ESTIMATE_LINE_ITEM_FIELD = "@estimateEditor/UPDATE_ESTIMATE_LINE_ITEM_FIELD";
export const REORDER_ESTIMATE_LINE_ITEM = "@estimateEditor/REORDER_ESTIMATE_LINE_ITEM";
export const MOVE_ESTIMATE_LINE_ITEM = "@estimateEditor/MOVE_ESTIMATE_LINE_ITEM";
export const UPDATE_ESTIMATE_LINE_ITEM_DEFAULTS = "@estimateEditor/UPDATE_ESTIMATE_LINE_ITEM_DEFAULTS";
export const APPLY_ESTIMATE_LINE_ITEM_DEFAULTS = "@estimateEditor/APPLY_ESTIMATE_LINE_ITEM_DEFAULTS";

// Estimate Line Item Opening Estimations
export const SET_LINE_ITEM_OPENING_ESTIMATIONS = "@estimateEditor/SET_LINE_ITEM_OPENING_ESTIMATIONS";

// Window Estimator (Openings Wizard)
export const APPLY_WINDOW_PRICE_ADJUSTMENT = "@estimateEditor/APPLY_WINDOW_PRICE_ADJUSTMENT";
export const CHANGE_LINE_ITEM_PRODUCT_WITH_OPTIONS = "@estimateEditor/CHANGE_LINE_ITEM_PRODUCT_WITH_OPTIONS";

// Estimate Line Item Options
export const ADD_ESTIMATE_LINE_ITEM_OPTION = "@estimateEditor/ADD_ESTIMATE_LINE_ITEM_OPTION";
export const REMOVE_ESTIMATE_LINE_ITEM_OPTION = "@estimateEditor/REMOVE_ESTIMATE_LINE_ITEM_OPTION";
export const UPDATE_ESTIMATE_LINE_ITEM_OPTION = "@estimateEditor/UPDATE_ESTIMATE_LINE_ITEM_OPTION";

// Estimate Discounts
export const ADD_ESTIMATE_DISCOUNT = "@estimateEditor/ADD_ESTIMATE_DISCOUNT";
export const REMOVE_ESTIMATE_DISCOUNT = "@estimateEditor/REMOVE_ESTIMATE_DISCOUNT";
export const UPDATE_ESTIMATE_DISCOUNT = "@estimateEditor/UPDATE_ESTIMATE_DISCOUNT";

// Sales Tax
export const UPDATE_SALES_TAX = "@estimateEditor/UPDATE_SALES_TAX";

// Estimate Payment Terms
export const ADD_ESTIMATE_PAYMENT_TERM = "@estimateEditor/ADD_ESTIMATE_PAYMENT_TERM";

// Estimate Payment Term Items
export const ADD_ESTIMATE_PAYMENT_TERM_ITEM = "@estimateEditor/ADD_ESTIMATE_PAYMENT_TERM_ITEM";
export const REMOVE_ESTIMATE_PAYMENT_TERM_ITEM = "@estimateEditor/REMOVE_ESTIMATE_PAYMENT_TERM_ITEM";
export const UPDATE_ESTIMATE_PAYMENT_TERM_ITEM = "@estimateEditor/UPDATE_ESTIMATE_PAYMENT_TERM_ITEM";

// Estimate Finance Options
export const ADD_ESTIMATE_FINANCE_OPTION = "@estimateEditor/ADD_ESTIMATE_FINANCE_OPTION";
export const REMOVE_ESTIMATE_FINANCE_OPTION = "@estimateEditor/REMOVE_ESTIMATE_FINANCE_OPTION";

// Validations
export const PATCH_LINE_ITEM_VALIDATIONS = "@estimateEditor/PATCH_LINE_ITEM_VALIDATIONS";

export const EstimateEditorActions = {
  /**
   * Creates a default unsaved estimate
   *
   * @param jobId The job id to associate the estimate with
   * @param kind The kind of estimate to create (default, inspection, survey). Defaults to "default"
   * @param groupsConfig Optional groups configuration. If provided, overrides org preferences and defaults
   * @param activatedPriceListId Optional price list to build against. Defaults to the org's default
   *   activated price list; inspections pass the org's activated inspection list.
   */
  createEstimate: (
    jobId: number,
    kind: EstimateKind = "default",
    groupsConfig?: IEstimateGroupConfig[],
    activatedPriceListId?: number,
  ) => createAction(CREATE_ESTIMATE, { jobId, kind, groupsConfig, activatedPriceListId }),
  duplicateEstimate: (estimateId: number) => createAction(DUPLICATE_ESTIMATE, { estimateId }),
  /**
   * Applies an estimate template to an estimate.
   * The template data must be fully hydrated with resolved product/option IDs.
   * The async action is responsible for loading the template from the API and
   * resolving all product/option UUIDs via the activated price list before dispatching.
   *
   * @param estimateId The id of the estimate to apply the template to
   * @param template The fully hydrated template data to apply
   * @param mode Whether to merge into or replace the estimate's existing groups
   */
  applyEstimateTemplate: (estimateId: number, template: IEstimateTemplateToApply, mode: ApplyTemplateMode = "merge") =>
    createAction(APPLY_ESTIMATE_TEMPLATE, { estimateId, template, mode }),
  updateEstimate: (event: FormUpdateEvent) => createAction(UPDATE_ESTIMATE, { event }),

  addEstimateGroup: (estimateId: number, estimateGroupData?: Partial<IEstimateGroupData>) =>
    createAction(ADD_ESTIMATE_GROUP, { estimateId, estimateGroupData }),
  removeEstimateGroup: (estimateId: number, estimateGroupId: number) =>
    createAction(REMOVE_ESTIMATE_GROUP, { estimateId, estimateGroupId }),
  updateEstimateGroup: (event: FormUpdateEvent) => createAction(UPDATE_ESTIMATE_GROUP, { event }),
  reorderEstimateGroup: (estimateId: number, oldIndex: number, newIndex: number) =>
    createAction(REORDER_ESTIMATE_GROUP, { estimateId, oldIndex, newIndex }),
  reorderGroupsInBlock: (estimateId: number, orderedGroupIds: number[]) =>
    createAction(REORDER_GROUPS_IN_BLOCK, { estimateId, orderedGroupIds }),

  addEstimateLineItem: (estimateGroupId: number, productId?: number) =>
    createAction(ADD_ESTIMATE_LINE_ITEM, { estimateGroupId, productId }),
  addCustomEstimateLineItem: (estimateGroupId: number, customProductData: ICustomProductData) =>
    createAction(ADD_CUSTOM_ESTIMATE_LINE_ITEM, { estimateGroupId, customProductData }),
  /**
   * Copies information from a product into an estimate line item
   * This is used after a line item is added to the estimate and a product is selected
   *
   * @param lineItemId The id of the line item to update
   * @param productId The product id to copy information from
   * @param options Optional swap flags: `keepDescription` preserves a user-edited description
   *   (skips the product-description overwrite); `keepQuantity` preserves a user-overridden quantity
   *   (skips the measurement-link recompute). Both default to false (full copy — the add-product path).
   */
  setEstimateLineItemProduct: (
    lineItemId: number,
    productId: number,
    options?: { keepDescription?: boolean; keepQuantity?: boolean },
  ) => createAction(SET_ESTIMATE_LINE_ITEM_PRODUCT, { lineItemId, productId, options }),
  removeEstimateLineItem: (estimateGroupId: number, lineItemId: number) =>
    createAction(REMOVE_ESTIMATE_LINE_ITEM, { estimateGroupId, lineItemId }),
  updateEstimateLineItem: (event: FormUpdateEvent) => createAction(UPDATE_ESTIMATE_LINE_ITEM, { event }),
  lineItemDragEnd: (sourceGroupId: number, destGroupId: number, oldVisibleIndex: number, newVisibleIndex: number) =>
    createAction(LINE_ITEM_DRAG_END, { sourceGroupId, destGroupId, oldVisibleIndex, newVisibleIndex }),
  reorderEstimateLineItem: (estimateGroupId: number, oldIndex: number, newIndex: number) =>
    createAction(REORDER_ESTIMATE_LINE_ITEM, { estimateGroupId, oldIndex, newIndex }),
  /**
   * Moves an estimate line item from one estimate group to another estimate group
   *
   * @param oldEstimateGroupId The id of the estimate group the line item is currently in
   * @param newEstimateGroupId The id of the estimate group to move the line item to
   * @param oldIndex The index of the line item to move
   * @param newIndex The index to move the line item to
   */
  moveEstimateLineItem: (oldEstimateGroupId: number, newEstimateGroupId: number, oldIndex: number, newIndex: number) =>
    createAction(MOVE_ESTIMATE_LINE_ITEM, { oldEstimateGroupId, newEstimateGroupId, oldIndex, newIndex }),
  updateEstimateLineItemDefaults: (estimateId: number, lineItemId: number) =>
    createAction(UPDATE_ESTIMATE_LINE_ITEM_DEFAULTS, { estimateId, lineItemId }),
  applyEstimateLineItemDefaults: (estimateId: number, lineItemId: number) =>
    createAction(APPLY_ESTIMATE_LINE_ITEM_DEFAULTS, { estimateId, lineItemId }),

  /**
   * Reconciles a line item's opening estimations to exactly the given openings (rsf-app
   * updateOpeningsSelection): still-selected openings keep their estimation (undeleting a
   * soft-deleted one), deselected persisted estimations are soft-deleted, deselected unsaved
   * ones are dropped, and newly selected openings get a new estimation.
   *
   * @param lineItemId The line item whose openings changed
   * @param openings The openings that should be attached (Window/Door records)
   */
  setLineItemOpeningEstimations: (lineItemId: number, openings: List<OpeningRecord>) =>
    createAction(SET_LINE_ITEM_OPENING_ESTIMATIONS, { lineItemId, openings }),

  /**
   * Applies the per_ui/per_sqft size-based price/name adjustment to a window/door line item
   * (rsf-app EstimateLineItem.windowPriceAdjustment). No-op when the product isn't a per_ui/per_sqft
   * bracket or the line item has no hydrated openings — see `windowPriceAdjustment`.
   *
   * @param lineItemId The line item to adjust
   * @param productId The bracket product on the line item (its base prices/measure drive the math)
   */
  applyWindowPriceAdjustment: (lineItemId: number, productId: number) =>
    createAction(APPLY_WINDOW_PRICE_ADJUSTMENT, { lineItemId, productId }),

  /**
   * Switches a line item to a new product WITHOUT re-seeding the product's default options
   * (rsf-app confirmedChangeProduct): copies the new product's fields, then clears the existing
   * options and re-adds only the `matchedOptions` carried over from the option comparison, each with
   * its preserved quantity. The wizard computes `matchedOptions` via `compareLineItemOptions`.
   *
   * @param lineItemId The line item being switched
   * @param productId The new product id
   * @param matchedOptions The options to carry over (new product's option/group ids + quantity)
   */
  changeLineItemProductWithOptions: (
    lineItemId: number,
    productId: number,
    matchedOptions: Array<{ productOptionId: number; productOptionGroupId: number; quantity: number }>,
  ) => createAction(CHANGE_LINE_ITEM_PRODUCT_WITH_OPTIONS, { lineItemId, productId, matchedOptions }),

  addEstimateLineItemOption: (
    lineItemId: number,
    productOptionId: number,
    productOptionGroupId?: number,
    quantity = 1,
  ) => createAction(ADD_ESTIMATE_LINE_ITEM_OPTION, { lineItemId, productOptionId, productOptionGroupId, quantity }),
  removeEstimateLineItemOption: (lineItemId: number, optionId: number) =>
    createAction(REMOVE_ESTIMATE_LINE_ITEM_OPTION, { lineItemId, optionId }),
  updateEstimateLineItemOption: (event: FormUpdateEvent) => createAction(UPDATE_ESTIMATE_LINE_ITEM_OPTION, { event }),

  addEstimateDiscount: (estimateId: number, discountId?: number) =>
    createAction(ADD_ESTIMATE_DISCOUNT, { estimateId, discountId }),
  removeEstimateDiscount: (estimateId: number, estimateDiscountIndex: number) =>
    createAction(REMOVE_ESTIMATE_DISCOUNT, { estimateId, estimateDiscountIndex }),
  updateEstimateDiscount: (event: FormUpdateEvent) => createAction(UPDATE_ESTIMATE_DISCOUNT, { event }),

  updateSalesTax: (event: FormUpdateEvent) => createAction(UPDATE_SALES_TAX, { event }),

  addEstimatePaymentTerm: (estimateId: number, paymentTermId: number) =>
    createAction(ADD_ESTIMATE_PAYMENT_TERM, { estimateId, paymentTermId }),

  addEstimatePaymentTermItem: (estimateId: number) => createAction(ADD_ESTIMATE_PAYMENT_TERM_ITEM, { estimateId }),
  removeEstimatePaymentTermItem: (estimateId: number, paymentTermItemUuid: string) =>
    createAction(REMOVE_ESTIMATE_PAYMENT_TERM_ITEM, { estimateId, paymentTermItemUuid }),
  updateEstimatePaymentTermItem: (event: FormUpdateEvent) => createAction(UPDATE_ESTIMATE_PAYMENT_TERM_ITEM, { event }),

  addEstimateFinanceOption: (estimateId: number, financeOptionId: number) =>
    createAction(ADD_ESTIMATE_FINANCE_OPTION, { estimateId, financeOptionId }),
  removeEstimateFinanceOption: (estimateId: number, estimateFinanceOptionId: number) =>
    createAction(REMOVE_ESTIMATE_FINANCE_OPTION, { estimateId, estimateFinanceOptionId }),

  patchLineItemValidations: (
    items: Array<{
      id: number;
      validated: boolean;
      validation_results: import("immutable").List<import("common/records/EstimateLineItem").ValidationResultRecord>;
    }>,
  ) => createAction(PATCH_LINE_ITEM_VALIDATIONS, { items }),

  /**
   * Starts adding a new line item to an estimate group: creates a product-less draft with a negative
   * unsaved id, snapshots it for dirty detection, and marks it as the session's new draft so Cancel
   * removes it (never restores it).
   *
   * @param estimateId The id of the estimate being edited
   * @param estimateGroupId The id of the estimate group to add the line item to
   */
  startAddingLineItem: (estimateId: number, estimateGroupId: number) =>
    createAction(START_ADDING_LINE_ITEM, { estimateId, estimateGroupId }),

  /**
   * Starts an editing session for a single line item. Snapshots ONLY the edited line item and its
   * owned options + opening-estimations for rollback. Callers editing an existing item pass its id;
   * the wizard create path omits it (the draft it then mints is marked explicitly and removed, not
   * restored, on cancel).
   *
   * @param estimateId The id of the estimate being edited
   * @param lineItemId The line item being edited (omit for the wizard create path)
   */
  startLineItemSession: (estimateId: number, lineItemId?: number) =>
    createAction(START_LINE_ITEM_SESSION, { estimateId, lineItemId }),

  /**
   * Marks an already-created line item as the session's new draft so Cancel removes it rather than
   * restoring it. Draft-ness is explicit and never inferred from snapshot absence — the Window
   * Estimator's create path uses this to flag the line item it mints (the "Add Line Item" path marks
   * its draft inside START_ADDING_LINE_ITEM).
   *
   * @param lineItemId The line item to mark as the current new draft
   */
  markLineItemDraft: (lineItemId: number) => createAction(MARK_LINE_ITEM_DRAFT, { lineItemId }),

  cancelLineItemSession: (lineItemId?: number) => createAction(CANCEL_LINE_ITEM_SESSION, { lineItemId }),
  commitLineItemSession: () => createAction(COMMIT_LINE_ITEM_SESSION, {}),
  cleanupDraftLineItems: () => createAction(CLEANUP_DRAFT_LINE_ITEMS, {}),
  recalculateEstimateTotals: (estimateId: number) => createAction(RECALCULATE_ESTIMATE_TOTALS, { estimateId }),
  /**
   * Commits the current (recalculated) group totals into estimateGroups.lastSavedById so the
   * estimate doesn't read dirty after a save when the server returned stale group subtotals. Dispatch
   * only on the save path, right after recalculateEstimateTotals — never on the apply path, where
   * lastSavedById must stay the untouched server baseline (the applied template leaves the estimate
   * dirty by design).
   *
   * @param estimateId The id of the estimate whose group totals to commit
   */
  commitGroupTotalsToLastSaved: (estimateId: number) => createAction(COMMIT_GROUP_TOTALS_TO_LAST_SAVED, { estimateId }),
  resetEstimate: () => createAction(RESET_ESTIMATE),

  /**
   * Updates a single field on an estimate line item directly (for controlled inputs)
   *
   * @param lineItemId The id of the line item to update
   * @param field The field name to update
   * @param value The new value for the field
   */
  updateEstimateLineItemField: (lineItemId: number, field: string, value: any) =>
    createAction(UPDATE_ESTIMATE_LINE_ITEM_FIELD, { lineItemId, field, value }),
};

export type EstimateEditorActions = ActionsUnion<typeof EstimateEditorActions>;
